import {
  forwardRef,
  type CSSProperties,
  type HTMLAttributes,
} from 'react';

import { cn } from '../../../utils/styles/cn';

import {
  LAYER_PRIORITY,
  CONTAINERS,
} from './constants';

/**
 * <Tooltip> — the outer wrapper that sets z-index and width for a
 * multi-section tooltip.
 */

type TooltipSize = 'small' | 'large' | 'fluid' | undefined;

export interface TooltipProps extends HTMLAttributes<HTMLDivElement> {
  priority?: number;
  size?: TooltipSize;
}

function sizeClass(size: TooltipSize): string {
  switch (size) {
    case 'small':
      return 'w-[14rem]';
    case 'large':
      return 'w-[20rem]';
    case 'fluid':
      return 'w-auto';
    default:
      return 'w-[18rem]';
  }
}

/**
 * The "every child but last has 0.5rem margin-bottom" rule lives in
 * global.css scoped to .cadactive-tooltip. (Was a JSX-rendered
 * <style> tag, but those don't always register their CSS rules under
 * React 18 + Vite dev — see the Loading fix.)
 */

const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(
  { priority, size, className, children, style, ...props },
  ref,
) {
  const zIndexStyle: CSSProperties = {
    zIndex: priority ?? LAYER_PRIORITY.DEFAULT,
    ...style,
  };

  return (
    <div
      ref={ref}
      className={cn('cadactive-tooltip', sizeClass(size), className)}
      style={zIndexStyle}
      {...props}
    >
      {children}
    </div>
  );
});

export {
  Tooltip,
  CONTAINERS,
  LAYER_PRIORITY,
};
